import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { Box, Typography, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface FinancialMetrics {
  purchased: number;
  baseline: number;
  forecast: number;
  accepted_client: number;
  invoiced: number;
}

export interface MonthlyIncomeData {
  monthly_income: {
    [year: string]: {
      [monthKey: string]: FinancialMetrics;
    };
  };
}

interface FinancialStatusBarChartProps {
  data: MonthlyIncomeData;
  symbol: any;
}

interface FlattenedChartData extends FinancialMetrics {
  uniqueMonthKey: string;
  displayLabel: string;
  year: string;
}

const colors: Record<string, string> = {
  purchased: '#3b82f6',
  baseline: '#a7f3d0',
  forecast: '#fbbf24',
  accepted_client: '#ef4444',
  invoiced: '#06b6d4',
};

const formatYAxis = (value: number, symbol: string) =>
  value === 0 ? `0 ${symbol}` : `${value.toLocaleString('fr-FR')} ${symbol}`;

const MuiCustomTooltip = (props: any) => {
  const { active, payload, label, symbol } = props;

  if (!active || !payload || !payload.length) {
    return null;
  }

  return (
    <Paper
      elevation={4}
      sx={{
        p: 1.5,
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        backgroundColor: '#ffffff',
        minWidth: 180,
        pointerEvents: 'none',
      }}
    >
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#64748b' }}>
          {label}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {payload.map((item: any) => (
          <Box
            key={item.dataKey}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: colors[item.dataKey as string] || item.color,
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                {item.name} {Number(item.value ?? 0)} {symbol}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

// 2. Main Chart Component
export const FinancialStatusBarChart: React.FC<FinancialStatusBarChartProps> = ({ data, symbol }) => {
  const [t] = useTranslation();
  const chartData = useMemo(() => {
    const flatList: FlattenedChartData[] = [];
    if (!data?.monthly_income) return flatList;

    const monthOrder: Record<string, number> = {
      jan: 1,
      feb: 2,
      mar: 3,
      apr: 4,
      may: 5,
      jun: 6,
      jul: 7,
      aug: 8,
      sep: 9,
      oct: 10,
      nov: 11,
      dec: 12,
    };

    Object.entries(data.monthly_income).forEach(([year, monthsObj]) => {
      Object.entries(monthsObj).forEach(([monthKey, metrics]) => {
        const uppercaseMonth = monthKey.charAt(0).toUpperCase() + monthKey.slice(1).toLowerCase();
        flatList.push({
          ...metrics,
          uniqueMonthKey: `${year}-${monthKey}`,
          displayLabel: `${uppercaseMonth}`,
          year,
        });
      });
    });

    // Sort chronologically
    flatList.sort((a, b) => {
      if (a.year !== b.year) {
        return parseInt(a.year, 10) - parseInt(b.year, 10);
      }
      const monthKeyA = a.uniqueMonthKey.split('-')[1].toLowerCase();
      const monthKeyB = b.uniqueMonthKey.split('-')[1].toLowerCase();
      return monthOrder[monthKeyA] - monthOrder[monthKeyB];
    });

    return flatList;
  }, [data]);

  const legendItems = [
    { key: 'purchased', label: t('Purchased') },
    { key: 'baseline', label: t('Baseline') },
    { key: 'forecast', label: t('Forecasted') },
    { key: 'accepted_client', label: t('Accepted_Client') },
    { key: 'invoiced', label: t('Invoiced') },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: '1px solid #e2e8f0',
        backgroundColor: '#fafafa',
        width: '100%',
      }}
    >
      {/* For Recharts 1.x, we must ensure height is tracked on an explicit parent container */}
      <Box sx={{ width: '100%', height: 240, position: 'relative' }}>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={chartData} margin={{ top: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="none" />
            <XAxis
              dataKey="displayLabel"
              tickLine={false}
              stroke="#333"
              tick={{ fontSize: 12, fontWeight: 500 }} // Typography styles handled inside the tick prop
            />
            <YAxis
              tickFormatter={(value) => formatYAxis(value, symbol)}
              width={80}
              tickLine={false}
              tick={{ fill: '#333', fontWeight: 500, fontSize: 14 }}
            />
            {/* Pass component reference directly rather than instantiation tag for 1.x compatibility */}
            <RechartsTooltip
              content={(props: any) => <MuiCustomTooltip {...props} symbol={symbol} />}
              cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
            />

            <Bar dataKey="purchased" name={t('Purchased')} fill={colors.purchased} minPointSize={1} />
            <Bar dataKey="baseline" name={t('Baseline')} fill={colors.baseline} minPointSize={1} />
            <Bar dataKey="forecast" name={t('Forecasted')} fill={colors.forecast} minPointSize={1} />
            <Bar dataKey="accepted_client" name={t('Accepted_Client')} fill={colors.accepted_client} minPointSize={1} />
            <Bar dataKey="invoiced" name={t('Invoiced')} fill={colors.invoiced} minPointSize={1} />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2.5,
          overflowX: 'auto',
          width: '100%',
        }}
      >
        {legendItems.map((item) => (
          <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: colors[item.key] }} />
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default FinancialStatusBarChart;
